import * as React from "react";
import { ChangeEvent, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { validateIntegerField, validateStringField } from "../../../utils/Validation";
import { Autocomplete, CircularProgress } from "@mui/material";
import { IIntegrationTestConfiguration } from "../../../services/registry/IntegrationTestConfigurationService";
import { BackendImagePicker } from "../../../components/BackendImagePicker";
import { IBackendImage } from "../../../services/registry/BackendImageService";
import { PackagePicker } from "../../../components/PackagePicker";
import { IPackage } from "../../../services/registry/PackageService";

export type IntegrationTestConfigurationFormType = "new" | "edit";

export interface IntegrationTestConfigurationFormData {
  clientBranch: string;
  backendImageId: string;
  activePackageId: string | undefined;
  activeSchemaBranch: string | undefined;
  activeSchemaVersion: number | undefined;
}

export interface IntegrationTestConfigurationFormProps {
  visible: boolean;
  type: IntegrationTestConfigurationFormType;
  initialData?: IIntegrationTestConfiguration;
  availableConfigurations?: IIntegrationTestConfiguration[];
  onDismiss: () => void;
  onAccept: (data: IntegrationTestConfigurationFormData) => Promise<void>;
}

export const IntegrationTestConfigurationFormField = (props: Omit<TextFieldProps, "variant">) => {
  return <TextField autoFocus margin="dense" fullWidth variant="standard" {...props} />;
};

export const IntegrationTestConfigurationForm = ({
  visible,
  type,
  initialData,
  availableConfigurations,
  onDismiss,
  onAccept,
}: IntegrationTestConfigurationFormProps) => {
  const [loading, setLoading] = useState(false);
  const [copyFromConfig, setCopyFromConfig] = useState<IIntegrationTestConfiguration | null>(null);
  const [clientBranchFormValue, setClientBranchFormValue] = useState<string>("");
  const [backendImageIdFormValue, setBackendImageIdFormValue] = useState<string | undefined>(undefined);
  const [activePackageIdFormValue, setActivePackageIdFormValue] = useState<string | undefined>(undefined);
  const [activeSchemaBranchFormValue, setActiveSchemaBranchFormValue] = useState<string>("");
  const [activeSchemaVersionFormValue, setActiveSchemaVersionFormValue] = useState<string>("");
  const [clientBranchError, setClientBranchError] = useState<string | undefined>(undefined);
  const [backendImageIdError, setBackendImageIdError] = useState<string | undefined>(undefined);
  const [activePackageIdError, setActivePackageIdError] = useState<string | undefined>(undefined);
  const [activeSchemaBranchError, setActiveSchemaBranchError] = useState<string | undefined>(undefined);
  const [activeSchemaVersionError, setActiveSchemaVersionError] = useState<string | undefined>(undefined);
  const [backendPickerPopupVisible, setBackendPickerPopupVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setCopyFromConfig(null);
      setClientBranchFormValue(initialData?.clientBranch || "");
      setBackendImageIdFormValue(initialData?.backendImageId);
      setActivePackageIdFormValue(initialData?.activePackageId);
      setActiveSchemaBranchFormValue(initialData?.activeSchemaBranch || "");
      setActiveSchemaVersionFormValue(initialData?.activeSchemaVersion?.toString() || "");
    }
  }, [visible, initialData]);

  useEffect(() => {
    if (copyFromConfig && type === "new") {
      setBackendImageIdFormValue(copyFromConfig.backendImageId);
      setActivePackageIdFormValue(copyFromConfig.activePackageId);
      setActiveSchemaBranchFormValue(copyFromConfig.activeSchemaBranch || "");
      setActiveSchemaVersionFormValue(copyFromConfig.activeSchemaVersion?.toString() || "");
    }
  }, [copyFromConfig, type]);

  const dialogCloseHandler = (_event: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const validateClientBranch = (value: string | undefined) => validateStringField(value, setClientBranchError, 3, 100);
  const validateBackendImageId = (value: string) => validateStringField(value, setBackendImageIdError, 1, 100);
  const validateActivePackageId = (value: string | undefined) => {
    if (!value) {
      return undefined;
    }
    return validateStringField(value, setActivePackageIdError, 1, 100);
  };
  const validateActiveSchemaBranch = (value: string) => {
    if (!value) {
      return undefined;
    }
    return validateStringField(value, setActiveSchemaBranchError, 1, 100);
  };
  const validateActiveSchemaVersion = (value: string) => {
    if (!value) {
      return undefined;
    }
    return validateIntegerField(value, setActiveSchemaVersionError, 1, 1000);
  };

  const clientBranchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setClientBranchFormValue(event.target.value);
    clientBranchError && validateClientBranch(event.target.value);
  };
  const backendImageIdChangeHandler = (event: ChangeEvent<HTMLInputElement>) =>
    backendImageIdError && validateBackendImageId(event.target.value);
  const activePackageIdChangeHandler = (event: ChangeEvent<HTMLInputElement>) =>
    activePackageIdError && validateActivePackageId(event.target.value);
  const activeSchemaBranchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setActiveSchemaBranchFormValue(event.target.value);
    activeSchemaBranchError && validateActiveSchemaBranch(event.target.value);
  };
  const activeSchemaVersionChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setActiveSchemaVersionFormValue(event.target.value);
    activeSchemaVersionError && validateActiveSchemaVersion(event.target.value);
  };

  const setBackendImageFromPicker = (backendImage: IBackendImage) => {
    setBackendImageIdFormValue(backendImage.id);
    setBackendPickerPopupVisible(false);
  };

  const setActivePackageIdFromPicker = (pkg: IPackage) => {
    setActivePackageIdFormValue(pkg.id);
    setActivePackageIdError(undefined);
  };

  const okButtonClickHandler = async () => {
    if (!backendImageIdFormValue) {
      return;
    }

    const clientBranch = validateClientBranch(clientBranchFormValue);
    const backendImageId = validateBackendImageId(backendImageIdFormValue);
    const activePackageId = validateActivePackageId(activePackageIdFormValue);
    const activeSchemaBranch = validateActiveSchemaBranch(activeSchemaBranchFormValue);
    const activeSchemaVersion = validateActiveSchemaVersion(activeSchemaVersionFormValue);

    if (!clientBranch || !backendImageId) {
      return;
    }

    setLoading(true);
    await onAccept({ clientBranch, backendImageId, activePackageId, activeSchemaBranch, activeSchemaVersion });
    setLoading(false);
  };

  return (
    <Dialog open={visible} onClose={dialogCloseHandler}>
      <DialogTitle>{(type === "new" ? "New" : "Edit") + " Integration Test Configuration"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <DialogContentText>Please fill up the information below:</DialogContentText>
            {type === "new" && availableConfigurations && availableConfigurations.length > 0 && (
              <Autocomplete
                options={availableConfigurations}
                getOptionLabel={(option) => option.clientBranch}
                value={copyFromConfig}
                onChange={(_, newValue) => setCopyFromConfig(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Copy from existing configuration"
                    margin="dense"
                    variant="standard"
                    helperText="Select a configuration to copy its fields (except Client Branch)"
                  />
                )}
                fullWidth
              />
            )}
            <IntegrationTestConfigurationFormField
              id="form-client-branch"
              label="Client Branch"
              type="text"
              value={clientBranchFormValue}
              error={clientBranchError !== undefined}
              helperText={clientBranchError}
              onChange={clientBranchChangeHandler}
              disabled={type !== "new"}
            />
            <IntegrationTestConfigurationFormField
              id="form-backend-image"
              label="Backend Image"
              type="text"
              value={backendImageIdFormValue || ""}
              error={backendImageIdError !== undefined}
              helperText={backendImageIdError}
              onChange={backendImageIdChangeHandler}
              onClick={() => setBackendPickerPopupVisible(true)}
            />
            <BackendImagePicker
              visible={backendPickerPopupVisible}
              dialogTitle="Select backend image"
              actionButtonLabel="Select"
              onCancel={() => setBackendPickerPopupVisible(false)}
              onAction={setBackendImageFromPicker}
            />
            <IntegrationTestConfigurationFormField
              id="form-active-schema-branch"
              label="Active Schema Branch"
              type="text"
              value={activeSchemaBranchFormValue}
              error={activeSchemaBranchError !== undefined}
              helperText={activeSchemaBranchError}
              onChange={activeSchemaBranchChangeHandler}
            />
            <IntegrationTestConfigurationFormField
              id="form-active-schema-version"
              label="Active Schema Version"
              type="text"
              value={activeSchemaVersionFormValue}
              error={activeSchemaVersionError !== undefined}
              helperText={activeSchemaVersionError}
              onChange={activeSchemaVersionChangeHandler}
            />
            <IntegrationTestConfigurationFormField
              id="form-active-package-id"
              label="Active Package ID"
              type="text"
              value={activePackageIdFormValue || ""}
              error={activePackageIdError !== undefined}
              helperText={activePackageIdError}
              onChange={activePackageIdChangeHandler}
            />
            <PackagePicker onPackageSelected={setActivePackageIdFromPicker} searchLabel="Search packages" />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="error" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={okButtonClickHandler} disabled={loading}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
